/**
 * WareBee Digital Twin - KPI Mock Data
 * Mock data for testing and development of KPI visualizations
 */

import { KPIGroup } from './kpiModel';

/**
 * Generate trend data for a KPI
 */
const generateTrendData = (
  baseValue: number,
  days: number,
  volatility: number = 0.05,
  trend: number = 0,
) => {
  const now = new Date();
  const data = [];

  for (let i = days; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    // Apply random volatility and trend
    const randomFactor = 1 + (Math.random() * 2 - 1) * volatility;
    const trendFactor = 1 + (trend * (days - i)) / days;
    const value = baseValue * randomFactor * trendFactor;

    data.push({
      date: date.toISOString().split('T')[0],
      value: parseFloat(value.toFixed(2)),
    });
  }

  return data;
};

/**
 * Generate forecast data for a KPI
 */
const generateForecastData = (
  lastValue: number,
  days: number,
  volatility: number = 0.08,
  trend: number = 0,
) => {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() + 1);

  const data = [];

  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);

    // Apply increasing volatility and trend for forecast
    const volatilityFactor = volatility * (1 + i / (days * 0.5));
    const randomFactor = 1 + (Math.random() * 2 - 1) * volatilityFactor;
    const trendFactor = 1 + (trend * i) / days;
    const value = lastValue * randomFactor * trendFactor;

    data.push({
      date: date.toISOString().split('T')[0],
      value: parseFloat(value.toFixed(2)),
      isForecasted: true,
    });
  }

  return data;
};

/**
 * Generate dimension breakdown data for a KPI
 */
const generateDimensionBreakdown = (
  dimension: string,
  totalValue: number,
  categories: string[],
) => {
  let remaining = totalValue;
  const results = [];

  // Generate random proportions for all but the last category
  for (let i = 0; i < categories.length - 1; i++) {
    // The proportion gets smaller as we get to later categories
    const proportion = Math.random() * (0.5 / (i + 1));
    const value = parseFloat((totalValue * proportion).toFixed(2));

    results.push({
      dimension,
      category: categories[i],
      value,
      percentage: parseFloat(((value / totalValue) * 100).toFixed(1)),
    });

    remaining -= value;
  }

  // Assign the remaining value to the last category
  results.push({
    dimension,
    category: categories[categories.length - 1],
    value: parseFloat(remaining.toFixed(2)),
    percentage: parseFloat(((remaining / totalValue) * 100).toFixed(1)),
  });

  return results;
};

/**
 * KPI mock data by category
 */
export const kpiMockData: Record<KPIGroup, any> = {
  // Warehouse Summary KPIs
  warehouse_summary: {
    overall_warehouse_efficiency: {
      current: 92.4,
      previous: 91.8,
      trend: generateTrendData(92, 90, 0.02, 0.01),
      forecast: generateForecastData(92.4, 30, 0.05, 0.01),
      dimensions: {
        process: generateDimensionBreakdown('process', 92.4, [
          'Picking',
          'Packing',
          'Shipping',
          'Receiving',
          'Put-away',
        ]),
      },
    },
    order_fulfillment_rate: {
      current: 97.2,
      previous: 96.8,
      trend: generateTrendData(97, 28, 0.01, 0.005),
      forecast: generateForecastData(97.2, 7, 0.02, 0.01),
      dimensions: {
        client_id: generateDimensionBreakdown('client_id', 97.2, [
          'Client A',
          'Client B',
          'Client C',
          'Client D',
          'Others',
        ]),
        order_type: generateDimensionBreakdown('order_type', 97.2, [
          'Standard',
          'Express',
          'Bulk',
          'Special',
        ]),
      },
    },
    warehouse_throughput: {
      current: 485,
      previous: 492,
      trend: generateTrendData(485, 28, 0.08, 0),
      forecast: generateForecastData(485, 7, 0.1, 0.05),
      dimensions: {
        order_type: generateDimensionBreakdown('order_type', 485, [
          'Standard',
          'Express',
          'Bulk',
          'Special',
        ]),
        hour_of_day: generateDimensionBreakdown('hour_of_day', 485, [
          'Morning',
          'Afternoon',
          'Evening',
        ]),
      },
    },
    cost_per_order: {
      current: 13.25,
      previous: 13.42,
      trend: generateTrendData(13.25, 90, 0.03, -0.01),
      forecast: generateForecastData(13.25, 14, 0.05, -0.02),
      dimensions: {
        order_complexity: generateDimensionBreakdown(
          'order_complexity',
          13.25,
          ['Simple', 'Medium', 'Complex'],
        ),
        client_id: generateDimensionBreakdown('client_id', 13.25, [
          'Client A',
          'Client B',
          'Client C',
          'Client D',
          'Others',
        ]),
      },
    },
  },

  // Today KPIs
  today: {
    orders_received_today: {
      current: 187,
      target: 200,
      trend: generateTrendData(187, 1, 0.2, 0),
      byHour: [
        { hour: '08:00', value: 12 },
        { hour: '09:00', value: 18 },
        { hour: '10:00', value: 24 },
        { hour: '11:00', value: 28 },
        { hour: '12:00', value: 22 },
        { hour: '13:00', value: 19 },
        { hour: '14:00', value: 26 },
        { hour: '15:00', value: 32 },
        { hour: '16:00', value: 21 },
        { hour: '17:00', value: 5 },
      ],
      dimensions: {
        client_id: generateDimensionBreakdown('client_id', 187, [
          'Client A',
          'Client B',
          'Client C',
          'Client D',
          'Others',
        ]),
      },
    },
    orders_shipped_today: {
      current: 175,
      target: 180,
      trend: generateTrendData(175, 1, 0.2, 0),
      byHour: [
        { hour: '08:00', value: 8 },
        { hour: '09:00', value: 15 },
        { hour: '10:00', value: 19 },
        { hour: '11:00', value: 24 },
        { hour: '12:00', value: 20 },
        { hour: '13:00', value: 18 },
        { hour: '14:00', value: 23 },
        { hour: '15:00', value: 28 },
        { hour: '16:00', value: 15 },
        { hour: '17:00', value: 5 },
      ],
      dimensions: {
        shipping_method: generateDimensionBreakdown('shipping_method', 175, [
          'Standard',
          'Express',
          'Next Day',
          'Same Day',
        ]),
      },
    },
    productivity_today: {
      current: 82,
      target: 85,
      trend: generateTrendData(82, 1, 0.1, 0),
      byHour: [
        { hour: '08:00', value: 78 },
        { hour: '09:00', value: 84 },
        { hour: '10:00', value: 87 },
        { hour: '11:00', value: 85 },
        { hour: '12:00', value: 79 },
        { hour: '13:00', value: 77 },
        { hour: '14:00', value: 83 },
        { hour: '15:00', value: 85 },
        { hour: '16:00', value: 82 },
        { hour: '17:00', value: 80 },
      ],
      dimensions: {
        process: generateDimensionBreakdown('process', 82, [
          'Picking',
          'Packing',
          'Shipping',
          'Receiving',
          'Put-away',
        ]),
      },
    },
  },

  // Process KPIs
  process: {
    uph_picking: {
      current: 118,
      previous: 115,
      trend: generateTrendData(118, 28, 0.06, 0.02),
      forecast: generateForecastData(118, 7, 0.07, 0.01),
      dimensions: {
        zone: generateDimensionBreakdown('zone', 118, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
        shift: generateDimensionBreakdown('shift', 118, [
          'Morning',
          'Afternoon',
          'Night',
        ]),
      },
    },
    putaway_cycle_time: {
      current: 22.5,
      previous: 23.8,
      trend: generateTrendData(22.5, 28, 0.08, -0.01),
      dimensions: {
        item_size: generateDimensionBreakdown('item_size', 22.5, [
          'Small',
          'Medium',
          'Large',
          'Extra Large',
        ]),
        storage_zone: generateDimensionBreakdown('storage_zone', 22.5, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
      },
    },
    picking_accuracy: {
      current: 99.7,
      previous: 99.6,
      trend: generateTrendData(99.7, 28, 0.001, 0.0005),
      dimensions: {
        picker_id: generateDimensionBreakdown('picker_id', 99.7, [
          'Team 1',
          'Team 2',
          'Team 3',
          'Team 4',
        ]),
        item_category: generateDimensionBreakdown('item_category', 99.7, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
      },
    },
    packing_rate: {
      current: 38.5,
      previous: 37.2,
      trend: generateTrendData(38.5, 28, 0.05, 0.01),
      dimensions: {
        packer_id: generateDimensionBreakdown('packer_id', 38.5, [
          'Team 1',
          'Team 2',
          'Team 3',
        ]),
        order_size: generateDimensionBreakdown('order_size', 38.5, [
          'Small',
          'Medium',
          'Large',
        ]),
      },
    },
  },

  // Inventory KPIs
  inventory: {
    inventory_accuracy: {
      current: 96.8,
      previous: 92.6,
      trend: generateTrendData(98.8, 90, 0.003, 0.001),
      dimensions: {
        location_class: generateDimensionBreakdown('location_class', 98.8, [
          'Rack',
          'Bulk',
          'Pallet',
          'Shelving',
        ]),
        sku_family: generateDimensionBreakdown('sku_family', 98.8, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
      },
    },
    days_of_supply: {
      current: 32.5,
      previous: 33.2,
      trend: generateTrendData(32.5, 90, 0.05, -0.005),
      forecast: generateForecastData(32.5, 30, 0.07, -0.01),
      dimensions: {
        product_category: generateDimensionBreakdown('product_category', 32.5, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
      },
    },
    inventory_turnover: {
      current: 11.2,
      previous: 10.8,
      trend: generateTrendData(11.2, 365, 0.02, 0.01),
      dimensions: {
        product_category: generateDimensionBreakdown('product_category', 11.2, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
        value_class: generateDimensionBreakdown('value_class', 11.2, [
          'A',
          'B',
          'C',
          'D',
        ]),
      },
    },
    storage_utilization: {
      current: 83.5,
      previous: 82.1,
      trend: generateTrendData(83.5, 28, 0.02, 0.005),
      dimensions: {
        storage_zone: generateDimensionBreakdown('storage_zone', 83.5, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
        location_type: generateDimensionBreakdown('location_type', 83.5, [
          'Rack',
          'Bulk',
          'Pallet',
          'Shelving',
        ]),
      },
    },
  },

  // Labour KPIs
  labour: {
    labor_utilization: {
      current: 87.5,
      previous: 86.8,
      trend: generateTrendData(87.5, 28, 0.03, 0.005),
      dimensions: {
        shift: generateDimensionBreakdown('shift', 87.5, [
          'Morning',
          'Afternoon',
          'Night',
        ]),
        department: generateDimensionBreakdown('department', 87.5, [
          'Picking',
          'Packing',
          'Shipping',
          'Receiving',
        ]),
      },
    },
    labor_efficiency: {
      current: 92.8,
      previous: 91.5,
      trend: generateTrendData(92.8, 28, 0.02, 0.01),
      dimensions: {
        process: generateDimensionBreakdown('process', 92.8, [
          'Picking',
          'Packing',
          'Shipping',
          'Receiving',
          'Put-away',
        ]),
        shift: generateDimensionBreakdown('shift', 92.8, [
          'Morning',
          'Afternoon',
          'Night',
        ]),
      },
    },
    overtime_percentage: {
      current: 7.2,
      previous: 7.8,
      trend: generateTrendData(7.2, 28, 0.1, -0.02),
      dimensions: {
        department: generateDimensionBreakdown('department', 7.2, [
          'Picking',
          'Packing',
          'Shipping',
          'Receiving',
        ]),
        day_of_week: generateDimensionBreakdown('day_of_week', 7.2, [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
        ]),
      },
    },
    worker_fatigue_index: {
      current: 35,
      previous: 32,
      trend: generateTrendData(35, 7, 0.1, 0.05),
      byHour: [
        { hour: '08:00', value: 20 },
        { hour: '09:00', value: 25 },
        { hour: '10:00', value: 30 },
        { hour: '11:00', value: 38 },
        { hour: '12:00', value: 45 },
        { hour: '13:00', value: 40 },
        { hour: '14:00', value: 35 },
        { hour: '15:00', value: 42 },
        { hour: '16:00', value: 48 },
        { hour: '17:00', value: 52 },
      ],
      dimensions: {
        process_type: generateDimensionBreakdown('process_type', 35, [
          'Picking',
          'Heavy Lifting',
          'Packing',
          'Walking',
        ]),
      },
    },
  },

  // Clients KPIs
  clients: {
    client_order_accuracy: {
      current: 98.5,
      previous: 98.2,
      trend: generateTrendData(98.5, 90, 0.003, 0.001),
      byClient: [
        { client: 'Client A', value: 99.1 },
        { client: 'Client B', value: 98.7 },
        { client: 'Client C', value: 98.3 },
        { client: 'Client D', value: 97.8 },
        { client: 'Client E', value: 98.5 },
      ],
      dimensions: {
        order_type: generateDimensionBreakdown('order_type', 98.5, [
          'Standard',
          'Express',
          'Bulk',
          'Special',
        ]),
      },
    },
    client_ontime_delivery: {
      current: 97.8,
      previous: 97.3,
      trend: generateTrendData(97.8, 28, 0.005, 0.002),
      byClient: [
        { client: 'Client A', value: 98.5 },
        { client: 'Client B', value: 97.9 },
        { client: 'Client C', value: 97.2 },
        { client: 'Client D', value: 96.8 },
        { client: 'Client E', value: 98.1 },
      ],
      dimensions: {
        shipping_method: generateDimensionBreakdown('shipping_method', 97.8, [
          'Standard',
          'Express',
          'Next Day',
          'Same Day',
        ]),
      },
    },
  },

  // Insights KPIs
  insights: {
    pick_path_efficiency: {
      current: 87.5,
      previous: 85.2,
      trend: generateTrendData(87.5, 28, 0.03, 0.02),
      dimensions: {
        zone: generateDimensionBreakdown('zone', 87.5, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
        order_size: generateDimensionBreakdown('order_size', 87.5, [
          'Small',
          'Medium',
          'Large',
        ]),
      },
      optimizationPotential: {
        savingsPerDay: '$235',
        walkingDistanceReduction: '420m per worker',
        timeReduction: '18 min per worker',
      },
    },
    slotting_optimization_potential: {
      current: 12.8,
      previous: 13.5,
      trend: generateTrendData(12.8, 90, 0.1, -0.01),
      dimensions: {
        product_category: generateDimensionBreakdown('product_category', 12.8, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
        velocity_class: generateDimensionBreakdown('velocity_class', 12.8, [
          'Fast',
          'Medium',
          'Slow',
        ]),
      },
      recommendations: [
        {
          sku: 'SKU12345',
          currentLocation: 'A42-B3',
          suggestedLocation: 'A12-B5',
          expectedImprovement: '25%',
        },
        {
          sku: 'SKU23456',
          currentLocation: 'C22-D7',
          suggestedLocation: 'A15-C2',
          expectedImprovement: '18%',
        },
        {
          sku: 'SKU34567',
          currentLocation: 'D15-A9',
          suggestedLocation: 'B22-D3',
          expectedImprovement: '15%',
        },
      ],
    },
    hidden_congestion_cost: {
      current: 180,
      previous: 210,
      trend: generateTrendData(180, 28, 0.15, -0.05),
      dimensions: {
        aisle: generateDimensionBreakdown('aisle', 180, [
          'Aisle 1',
          'Aisle 2',
          'Aisle 3',
          'Aisle 4',
          'Aisle 5',
        ]),
        time_of_day: generateDimensionBreakdown('time_of_day', 180, [
          'Morning',
          'Afternoon',
          'Evening',
        ]),
      },
      hotspots: [
        {
          location: 'Aisle 3 - Section B',
          congestionLevel: 'High',
          waitTime: '4.2 min',
          costImpact: '$45/day',
        },
        {
          location: 'Aisle 1 - Section D',
          congestionLevel: 'Medium',
          waitTime: '2.8 min',
          costImpact: '$32/day',
        },
        {
          location: 'Packing Area B',
          congestionLevel: 'Medium',
          waitTime: '3.1 min',
          costImpact: '$28/day',
        },
      ],
    },
  },

  // Issues KPIs
  issues: {
    stock_discrepancy_rate: {
      current: 2.3,
      previous: 2.5,
      trend: generateTrendData(2.3, 90, 0.1, -0.01),
      dimensions: {
        zone: generateDimensionBreakdown('zone', 2.3, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
        product_category: generateDimensionBreakdown('product_category', 2.3, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
      },
      topDiscrepancies: [
        {
          sku: 'SKU45678',
          location: 'B22-C4',
          systemQty: 15,
          actualQty: 12,
          lastActivity: 'Picking',
        },
        {
          sku: 'SKU56789',
          location: 'A14-D7',
          systemQty: 8,
          actualQty: 10,
          lastActivity: 'Receiving',
        },
        {
          sku: 'SKU67890',
          location: 'C31-B2',
          systemQty: 25,
          actualQty: 22,
          lastActivity: 'Cycle Count',
        },
      ],
    },
    mispick_rate: {
      current: 0.35,
      previous: 0.42,
      trend: generateTrendData(0.35, 28, 0.2, -0.05),
      dimensions: {
        picker_id: generateDimensionBreakdown('picker_id', 0.35, [
          'Team 1',
          'Team 2',
          'Team 3',
          'Team 4',
        ]),
        product_category: generateDimensionBreakdown('product_category', 0.35, [
          'Electronics',
          'Clothing',
          'Food',
          'Home Goods',
          'Other',
        ]),
      },
    },
  },

  // Compliance KPIs
  compliance: {
    safety_incident_rate: {
      current: 3.2,
      previous: 3.5,
      trend: generateTrendData(3.2, 365, 0.1, -0.02),
      dimensions: {
        incident_type: generateDimensionBreakdown('incident_type', 3.2, [
          'Slip/Fall',
          'Strain/Sprain',
          'Equipment Related',
          'Other',
        ]),
        location: generateDimensionBreakdown('location', 3.2, [
          'Picking Area',
          'Loading Dock',
          'Storage Racks',
          'Packing Station',
          'Other',
        ]),
      },
      recentIncidents: [
        {
          date: '2024-03-15',
          type: 'Slip/Fall',
          location: 'Aisle B',
          severity: 'Minor',
        },
        {
          date: '2024-02-22',
          type: 'Strain',
          location: 'Loading Dock',
          severity: 'Moderate',
        },
        {
          date: '2024-01-18',
          type: 'Equipment',
          location: 'Packing Area',
          severity: 'Minor',
        },
      ],
    },
    hazmat_compliance_rate: {
      current: 98.5,
      previous: 97.8,
      trend: generateTrendData(98.5, 28, 0.01, 0.005),
      dimensions: {
        hazmat_class: generateDimensionBreakdown('hazmat_class', 98.5, [
          'Class 1',
          'Class 2',
          'Class 3',
          'Class 4',
        ]),
        zone: generateDimensionBreakdown('zone', 98.5, [
          'Zone A',
          'Zone B',
          'Zone C',
          'Zone D',
        ]),
      },
      nonCompliantLocations: [
        {
          location: 'H12-B5',
          hazmatClass: 'Class 2',
          issue: 'Improper Labeling',
          lastInspection: '2024-03-10',
        },
        {
          location: 'H08-D3',
          hazmatClass: 'Class 3',
          issue: 'Storage Proximity',
          lastInspection: '2024-03-12',
        },
      ],
    },
  },

  // Projects KPIs
  projects: {
    automation_roi: {
      current: 1.25,
      target: 1.5,
      trend: generateTrendData(1.25, 120, 0.05, 0.01),
      phases: [
        { phase: 'Phase 1', completion: '100%', roi: 1.1 },
        { phase: 'Phase 2', completion: '75%', roi: 1.25 },
        { phase: 'Phase 3', completion: '10%', roi: null },
      ],
      projectedAnnualSavings: '$425,000',
    },
    process_improvement_impact: {
      projects: [
        {
          name: 'Conveyor System Upgrade',
          status: 'In Progress',
          completion: 65,
          impact: { throughput: '+12%', labor: '-8%' },
        },
        {
          name: 'Pick-to-Light Implementation',
          status: 'Planning',
          completion: 20,
          impact: { accuracy: '+1.2%', speed: '+15%' },
        },
        {
          name: 'Warehouse Layout Optimization',
          status: 'Completed',
          completion: 100,
          impact: { walkingDistance: '-18%', utilization: '+7%' },
        },
      ],
    },
  },

  // Heatmaps KPIs
  heatmaps: {
    zone_activity_density: {
      timestamp: new Date().toISOString(),
      gridSize: { width: 20, height: 15 },
      data: Array(20 * 15)
        .fill(0)
        .map((_, i) => ({
          x: i % 20,
          y: Math.floor(i / 20),
          value: Math.random() * 100,
        })),
      hotspots: [
        { x: 5, y: 3, value: 95, label: 'High traffic junction' },
        { x: 12, y: 8, value: 88, label: 'Picking station A' },
        { x: 18, y: 12, value: 92, label: 'Shipping area' },
      ],
    },
    congestion_heatmap: {
      timestamp: new Date().toISOString(),
      gridSize: { width: 20, height: 15 },
      data: Array(20 * 15)
        .fill(0)
        .map((_, i) => ({
          x: i % 20,
          y: Math.floor(i / 20),
          value: Math.random() * 75,
        })),
      congestionPoints: [
        { x: 8, y: 5, value: 85, waitTime: '3.5 min' },
        { x: 15, y: 7, value: 78, waitTime: '2.8 min' },
        { x: 4, y: 11, value: 82, waitTime: '3.2 min' },
      ],
    },
  },

  // Data I/O KPIs
  data_io: {
    data_capture_accuracy: {
      current: 98.7,
      previous: 98.3,
      trend: generateTrendData(98.7, 28, 0.01, 0.005),
      dimensions: {
        scanner_type: generateDimensionBreakdown('scanner_type', 98.7, [
          'Handheld',
          'Fixed',
          'Wearable',
          'Mobile',
        ]),
        item_category: generateDimensionBreakdown('item_category', 98.7, [
          'Barcoded',
          'RFID-Tagged',
          'Oversized',
          'Hazmat',
        ]),
      },
    },
    system_integration_error_rate: {
      current: 0.45,
      previous: 0.52,
      trend: generateTrendData(0.45, 28, 0.15, -0.02),
      dimensions: {
        integration_type: generateDimensionBreakdown('integration_type', 0.45, [
          'WMS',
          'OMS',
          'TMS',
          'ERP',
          'Other',
        ]),
        error_type: generateDimensionBreakdown('error_type', 0.45, [
          'Timeout',
          'Data Format',
          'Authentication',
          'Network',
          'Unknown',
        ]),
      },
      recentErrors: [
        {
          timestamp: '2024-03-15T14:22:38Z',
          system: 'WMS',
          type: 'Timeout',
          status: 'Resolved',
        },
        {
          timestamp: '2024-03-14T09:17:12Z',
          system: 'ERP',
          type: 'Data Format',
          status: 'Resolved',
        },
        {
          timestamp: '2024-03-10T16:05:45Z',
          system: 'OMS',
          type: 'Authentication',
          status: 'Resolved',
        },
      ],
    },
  },
  kpi: undefined,
  activity: undefined,
  analyze: undefined,
  costs: undefined,
  operations: undefined,
  uom: undefined,
  cost: undefined,
  throughput: undefined,
  actual: undefined,
  efficiency: undefined,
  performance: undefined,
  productivity: undefined,
  planned: undefined,
  iot: undefined,
  digital_twin_simulation: undefined,
  general: undefined,
};

export default kpiMockData;
